import { useEffect, useRef, type ReactNode } from 'react'
import { motion } from 'framer-motion'

interface MegaPanelProps {
  isOpen: boolean
  onRequestClose: () => void
  /** CSS top value (number = pixels, string = any CSS length, e.g. 'calc(56px + var(--banner-stack-height, 0px))'). */
  topPx: number | string
  children: ReactNode
}

/**
 * Full-bleed dropdown drawer that sits flush under the main nav bar.
 * Edge-to-edge background, max-w container inside. Closes on Escape and
 * on click-outside.
 */
export default function MegaPanel({
  isOpen,
  onRequestClose,
  topPx,
  children,
}: MegaPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onRequestClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onRequestClose])

  return (
    <motion.div
      ref={panelRef}
      initial={false}
      animate={isOpen ? { opacity: 1, y: 0, pointerEvents: 'auto' } : { opacity: 0, y: -8, pointerEvents: 'none' }}
      transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="fixed left-0 right-0 z-30 px-4 sm:px-6 lg:px-8 pt-3"
      style={{ top: topPx }}
      onMouseLeave={onRequestClose}
      aria-hidden={!isOpen}
    >
      {/* Floating card — same frame vocabulary as the founder-video on the
          About page: rounded-2xl + gold ring + ivory-dark moat + gold-lift
          shadow. The 6px ivory-dark moat sits on top of the dark header
          chrome and reads as a clean ivory-dark strip framing the gold ring. */}
      <div className="max-w-7xl mx-auto bg-ivory rounded-2xl ring-1 ring-gold/40 ring-offset-[6px] ring-offset-ivory-dark shadow-[var(--shadow-gold-lift)] overflow-hidden">
        <div className="px-6 sm:px-8 lg:px-10 py-6 md:py-7">{children}</div>
      </div>
    </motion.div>
  )
}
